import { API_BASE } from "./config";
import { bounceIfUnauthorized } from "./session";

/**
 * Reading logs — the record of what you read, and where it is filed (日本-207).
 *
 * **Three nouns, settled in the design handoff and used verbatim everywhere.** A **tab** is a slot
 * in the strip: cheap, several open at once, opening and closing one is not a data operation. A
 * **log** is the record itself — the noun you name, file, move and delete. The **library** is where
 * logs live, and it is a sub-view of Read rather than a rail destination. A **folder** is one level
 * only, and it is a *work*: サクラノ詩, Muv-Luv Alternative.
 *
 * Getting those apart is what stops the feature collapsing: "tab for everything" borrows the half
 * of the browser model where closing is free, and here closing a tab must never mean losing 400
 * mined sentences.
 *
 * **This is server state, and that is the whole point.** It used to be per-session memory, which
 * meant a rail click destroyed it (日本-198) and a reload certainly did. What is still local — and
 * deliberately so — is which tabs are open, which is active, and which sentence has focus. See
 * `surfaces/read/logTabs.ts`.
 */

/** A work. One level of folders, deliberately — see `libraryRows.ts` for what that buys. */
export interface ReadingFolder {
  id: string;
  name: string;
  sortOrder: number;
  logCount: number;
  sentenceCount: number;
}

export interface ReadingLog {
  id: string;
  /**
   * `null` is the **unsaved** state, and it is not a separate kind of object.
   *
   * A fresh `+` gives a real, capturing, persisted log that is simply not filed yet — shown as
   * `New log N` in the strip and listed under `Unfiled` in the library. Listed, not hidden: a
   * scratchpad you have forgotten about is what eats a month of captures.
   *
   * There is no `unsaved` flag beside this on purpose. Two spellings of one fact are two rules to
   * keep in step.
   */
  name: string | null;
  /**
   * A permanent number, behind the `New log 7` label an unnamed log shows.
   *
   * **From a Postgres sequence, so it is never reused.** The label used to be the log's *position*
   * among the unsaved logs that existed, which meant closing `New log 2` renamed 3-8 to 2-7 —
   * every remaining tab silently changing its name at once. A number you can say out loud has to
   * survive its neighbours going away.
   */
  seq: number;
  folderId: string | null;
  createdAt: number;
  lastReadAt: number;
  sortOrder: number;
  sentenceCount: number;
  minedCount: number;
}

export interface ReadingSentence {
  id: string;
  logId: string;
  /** Seconds since the epoch. The log's sort key, and what a moved sentence keeps. */
  capturedAt: number;
  text: string;
  /**
   * Anki **field values**, not filenames — `<img src="capture:…">`.
   *
   * The invariant `surfaces/read/mediaSlots.ts` states: a slot holds what a mined card inherits
   * verbatim. Persisting the slot changes nothing about that, which is why nothing on this round
   * trip unwraps one.
   */
  image: string;
  extraImage: string;
  /** The notes mined from this sentence. Real ids, so the library can count them and say so. */
  minedNoteIds: number[];
  /**
   * `"camera"` while `image` is a straightened phone photo standing in for a screenshot (日本-711).
   * Drawn nowhere — Alex's call; it exists for the MCP (日本-712) — and optional so a response from a
   * server older than the field still parses.
   */
  imageSource?: string;
  photoTakenAt?: string | null;
  /**
   * The English, when it was asked for (日本-102). Empty means bare.
   *
   * **Never automatic**, and never copied onto a mined card: a model's sentence is not one to
   * review against for two years. It rides on the sentence rather than being fetched per row, so
   * opening a log brings its translations in the one request it already makes.
   */
  translation: string;
}

export interface ReadingLibrary {
  folders: ReadingFolder[];
  logs: ReadingLog[];
}

export interface ReadingLogDetail {
  log: ReadingLog;
  sentences: ReadingSentence[];
}

async function json<T>(path: string, init?: RequestInit, what = "read the logs"): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, { cache: "no-store", ...init });
  if (!res.ok) {
    bounceIfUnauthorized(res);
    const detail = await res
      .json()
      .then((b) => (b as { detail?: string }).detail)
      .catch(() => undefined);
    throw new Error(detail ?? `Could not ${what} (${res.status})`);
  }
  if (res.status === 204) return undefined as T;
  return res.json();
}

function send<T>(path: string, method: string, body: unknown, what: string): Promise<T> {
  return json<T>(
    path,
    {
      method,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    },
    what,
  );
}

/** Folders and logs with their counts — and no sentences. 31 rows, not 6,000. */
export function fetchLibrary(): Promise<ReadingLibrary> {
  return json<ReadingLibrary>("/api/reading/library", undefined, "read the library");
}

export function fetchLog(id: string): Promise<ReadingLogDetail> {
  return json<ReadingLogDetail>(`/api/reading/logs/${id}`, undefined, "open the log");
}

/**
 * Create a log under an id **this client minted**.
 *
 * The server treats a repeat as the same log rather than a conflict, which is what makes a retry
 * after a lost response safe. See `useReading` for why the id cannot come from the server: a
 * capture needs one synchronously, before the sentence has even been parsed.
 */
export function createLog(
  id: string,
  body: { name?: string | null; folderId?: string | null } = {},
): Promise<ReadingLog> {
  return send<ReadingLog>("/api/reading/logs", "POST", { id, ...body }, "create the log");
}

/**
 * Rename, file, reorder, or mark as read.
 *
 * **Send only the keys you mean.** `null` is a real value for both `name` (un-name it) and
 * `folderId` (move it to the root), so the server distinguishes absent from null — spreading a
 * whole log object in here would unfile it on every rename.
 */
export function updateLog(
  id: string,
  body: {
    name?: string | null;
    folderId?: string | null;
    sortOrder?: number;
    touch?: boolean;
  },
): Promise<ReadingLog> {
  return send<ReadingLog>(`/api/reading/logs/${id}`, "PATCH", body, "save the log");
}

/** Answers what it took, so a confirmation can be checked against the outcome. */
export function deleteLog(id: string): Promise<{ sentences: number; mined: number }> {
  return json(`/api/reading/logs/${id}`, { method: "DELETE" }, "delete the log");
}

/** Empty a log, keeping it. The unsaved log's destructive action, which does not ask first. */
export function clearLog(id: string): Promise<{ sentences: number }> {
  return json(`/api/reading/logs/${id}/clear`, { method: "POST" }, "clear the log");
}

export function addSentence(
  logId: string,
  body: {
    id: string;
    text: string;
    capturedAt?: number;
    image?: string;
    extraImage?: string;
    /** Set once, at creation, by the photo path only; the server clears it when `image` changes. */
    imageSource?: "camera";
    photoTakenAt?: string | null;
  },
): Promise<ReadingSentence> {
  return send<ReadingSentence>(
    `/api/reading/logs/${logId}/sentences`,
    "POST",
    body,
    "save the sentence",
  );
}

/**
 * Edit a sentence, or move it to another log.
 *
 * **A move is `logId` alone.** The timestamp, both images and the mined-card links travel with the
 * row, and `capturedAt` staying put is what lands it in timestamp order at the far end rather than
 * at the bottom — a log is a record of reading, so a moved sentence must not claim it was read
 * last.
 *
 * Keyed by sentence id and not by log, which is what lets a screenshot finish uploading after you
 * have switched tabs.
 */
export function updateSentence(
  id: string,
  body: {
    text?: string;
    image?: string;
    extraImage?: string;
    logId?: string;
    /**
     * **Both editing and removing a translation.** `""` returns the row to bare, because the empty
     * string *is* the bare state — so `remove` is a write rather than a route of its own.
     * Producing one is `translate.ts`; this is only where the reader's own correction lands.
     */
    translation?: string;
  },
): Promise<ReadingSentence> {
  return send<ReadingSentence>(`/api/reading/sentences/${id}`, "PATCH", body, "save the sentence");
}

/** Record a card mined from this sentence. Idempotent on the note id. */
export function noteMined(id: string, noteId: number): Promise<ReadingSentence> {
  return send<ReadingSentence>(
    `/api/reading/sentences/${id}/mined`,
    "POST",
    { noteId },
    "record the mined card",
  );
}

export function deleteSentence(id: string): Promise<void> {
  return json(`/api/reading/sentences/${id}`, { method: "DELETE" }, "delete the sentence");
}

export function createFolder(id: string, name: string): Promise<ReadingFolder> {
  return send<ReadingFolder>("/api/reading/folders", "POST", { id, name }, "create the folder");
}

export function updateFolder(
  id: string,
  body: { name?: string; sortOrder?: number },
): Promise<ReadingFolder> {
  return send<ReadingFolder>(`/api/reading/folders/${id}`, "PATCH", body, "save the folder");
}

/** Delete a folder. **Its logs survive, at the root** — deleting a shelf is not deleting the books. */
export function deleteFolder(id: string): Promise<void> {
  return json(`/api/reading/folders/${id}`, { method: "DELETE" }, "delete the folder");
}

/**
 * A fresh id for a log, a folder or a sentence.
 *
 * **Minted here rather than by the server**, and that is forced rather than tidy: `append` has to
 * hand an id to the tray agent's screenshot upload *synchronously*, before the parse request has
 * even been sent — see `useReadingLog`'s note on `onCreated`. A server-assigned id would have to be
 * reconciled back through every piece of Read's state that keys on one.
 *
 * `crypto.randomUUID` is not available on an insecure origin, which `npm run dev` over a LAN
 * address is — so there is a fallback rather than a crash on the one path that must never fail.
 */
export function newId(): string {
  try {
    return crypto.randomUUID().replace(/-/g, "");
  } catch {
    return `${Date.now().toString(16)}${Math.random().toString(16).slice(2, 10)}`;
  }
}
